// App-level providers. Plan 20 establishes the shell:
//   - QueryClientProvider (single QueryClient for the whole app)
//   - ReactQueryDevtools (only meaningful in dev)
//   - ErrorBoundary
//   - AuthProvider (see features/auth/AuthProvider.tsx)
//   - CartProvider (plan 23 — cart state with sessionStorage persistence)
//
// Plan 22 adds the CSRF bootstrap — a one-shot `GET /api/csrf-token` that
// seeds the `hd_csrf` cookie `safeFetch` reads on every mutation. The
// bootstrap lives inside its own small component so it sits under
// QueryClientProvider but does not affect the rendered tree shape.
//
// Plan 23 tightens the cache config (1-min staleTime, 30-min gcTime,
// no window-focus refetch, no mutation retries) and fires a non-blocking
// prefetch of `['services']` + `['me']` so the landing page has data
// before the first paint resolves.

import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from './ErrorBoundary';
import { AuthProvider } from '../features/auth/AuthProvider';
import { CartProvider } from '../features/booking/CartProvider';
import { useCsrfBootstrap } from '../shared/api/csrf';
import { authKeys, serviceKeys } from '../shared/api/queryKeys';
import { fetchServices } from '../features/services/servicesApi';
import { fetchMe } from '../features/auth/authApi';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 min — matches plan 23
      gcTime: 1000 * 60 * 30, // 30 min
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

/**
 * Side-effect-only component: fires `GET /api/csrf-token` on mount so the
 * `hd_csrf` cookie is seeded before the user triggers any mutation. The
 * request is idempotent and dedupes by query key, so a double-render under
 * React's StrictMode is free.
 */
const CsrfBootstrap = () => {
  useCsrfBootstrap();
  return null;
};

/**
 * Fire-and-forget prefetch of the two queries every route of the SPA
 * touches: the services catalog (landing page) and the current user
 * (navbar / dashboard guards). Non-blocking — `prefetchQuery` returns a
 * promise we intentionally swallow; React Query keeps whatever resolves
 * as the cached value, and a failure leaves the query in `idle` for the
 * component-level hooks to handle.
 */
const BootstrapPrefetch = () => {
  useEffect(() => {
    void queryClient.prefetchQuery({
      queryKey: serviceKeys.list(),
      queryFn: fetchServices,
    });
    void queryClient.prefetchQuery({
      queryKey: authKeys.me(),
      queryFn: fetchMe,
    });
  }, []);
  return null;
};

export const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CsrfBootstrap />
        <BootstrapPrefetch />
        <AuthProvider>
          <CartProvider>{children}</CartProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
